import { SearchBox } from '@mapbox/search-js-react';
import { Box, GlobalStyles } from '@mui/material';
import React, { useState, useCallback, useEffect } from 'react';

import { environment } from '@env';

import { Feature } from '@/types/mapbox';

import { Home01Icon } from '@/assets/icons/Home01Icon';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';

// Type definitions for Mapbox SearchBox results
interface MapboxFeature {
  id?: string | number;
  geometry?: {
    coordinates?: number[];
  };
  properties?: {
    mapbox_id?: string;
    full_address?: string;
    place_formatted?: string;
    address?: string;
    name?: string;
    feature_type?: string;
    context?: {
      country?: {
        name?: string;
        country_code?: string;
        country_code_alpha_3?: string;
      };
      district?: {
        name?: string;
      };
      place?: {
        name?: string;
      };
      postcode?: {
        name?: string;
      };
      region?: {
        name?: string;
        region_code?: string;
        region_code_full?: string;
      };
      street?: {
        name?: string;
      };
    };
  };
}

interface MapboxRetrieveResult {
  features?: MapboxFeature[];
}

interface MapboxSuggestResult {
  suggestion?: {
    name?: string;
    full_address?: string;
  };
}

interface SearchBoxProps {
  accessToken: string;
  value: string;
  onChange: (value: string) => void;
  onRetrieve: (result: MapboxRetrieveResult) => void;
  onSuggest: (result: MapboxSuggestResult) => void;
  onClear: () => void;
  options?: {
    language?: string;
    country?: string;
    limit?: number;
  };
  theme?: {
    variables?: {
      fontFamily?: string;
      padding?: string;
      borderRadius?: string;
      border?: string;
      boxShadow?: string;
    };
  };
  placeholder?: string;
  disabled?: boolean;
}

interface MapboxAddressAutocompleteProps {
  value: Feature | null;
  onChange: (value: Feature | null) => void;
  placeholder?: string;
  disabled?: boolean;
  sx?: Record<string, unknown>;
  size?: 'small' | 'medium';
}

const MapboxAddressAutocomplete: React.FC<MapboxAddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'my AC stopped working or I need to build a small patio',
  disabled = false,
  sx = {},
  size = 'medium',
}) => {
  const [searchValue, setSearchValue] = useState('');

  // Update search value when value prop changes
  useEffect(() => {
    if (value) {
      const displayValue =
        value.properties.full_address ||
        value.properties.place_formatted ||
        value.properties.name ||
        '';
      setSearchValue(displayValue);
    } else {
      setSearchValue('');
    }
  }, [value]);

  const handleRetrieve = useCallback(
    (result: MapboxRetrieveResult) => {
      // Convert Mapbox SearchBox result to our Feature format
      if (result?.features?.[0]) {
        const feature = result.features[0];
        const coords = feature.geometry?.coordinates || [0, 0];

        // Create a Feature object that matches our interface
        const convertedFeature: Feature = {
          id: String(feature.id || ''),
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [coords[0] || 0, coords[1] || 0] as [number, number],
          },
          properties: {
            mapbox_id: String(feature.properties?.mapbox_id || feature.id || ''),
            full_address: String(
              feature.properties?.full_address ||
                feature.properties?.place_formatted ||
                feature.properties?.address ||
                feature.properties?.name ||
                ''
            ),
            name: String(feature.properties?.name || ''),
            place_formatted: String(feature.properties?.place_formatted || ''),
            feature_type: String(feature.properties?.feature_type || 'address'),
            coordinates: {
              accuracy: 'point',
              latitude: coords[1] || 0,
              longitude: coords[0] || 0,
              routable_points: [],
            },
            context: {
              address: {
                mapbox_id: '',
                name: String(feature.properties?.address || ''),
                address: String(feature.properties?.address || ''),
                street_name: '',
              },
              country: {
                mapbox_id: '',
                name: String(feature.properties?.context?.country?.name || 'United States'),
                country_code: String(feature.properties?.context?.country?.country_code || 'US'),
                country_code_alpha_3: String(
                  feature.properties?.context?.country?.country_code_alpha_3 || 'USA'
                ),
              },
              district: {
                mapbox_id: '',
                name: String(feature.properties?.context?.district?.name || ''),
              },
              place: {
                mapbox_id: '',
                name: String(feature.properties?.context?.place?.name || ''),
              },
              postcode: {
                mapbox_id: '',
                name: String(feature.properties?.context?.postcode?.name || ''),
              },
              region: {
                mapbox_id: '',
                name: String(feature.properties?.context?.region?.name || ''),
                region_code: String(feature.properties?.context?.region?.region_code || ''),
                region_code_full: String(
                  feature.properties?.context?.region?.region_code_full || ''
                ),
              },
              street: {
                mapbox_id: '',
                name: String(feature.properties?.context?.street?.name || ''),
              },
            },
            match_code: {
              address_number: '',
              confidence: '',
              country: '',
              locality: '',
              place: '',
              postcode: '',
              region: '',
              street: '',
            },
            name_preferred: String(feature.properties?.name || ''),
          },
        };

        onChange(convertedFeature);
        setSearchValue(convertedFeature.properties.full_address);
      }
    },
    [onChange]
  );

  const handleSuggest = useCallback((result: MapboxSuggestResult) => {
    // Handle suggestion selection
    if (result?.suggestion) {
      setSearchValue(result.suggestion.name || result.suggestion.full_address || '');
    }
  }, []);

  const handleClear = useCallback(() => {
    onChange(null);
    setSearchValue('');
  }, [onChange]);

  return (
    <Box sx={{ position: 'relative', width: '100%', ...sx }}>
      {/* Custom search icon overlay */}
      <Box
        sx={{
          position: 'absolute',
          left: '14px',
          top: '55%',
          transform: 'translateY(-50%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <Home01Icon sx={{ width: 20, height: 20, color: '#666' }} />
      </Box>

      {/* Clear button overlay */}
      {searchValue && (
        <Box
          onClick={handleClear}
          sx={{
            position: 'absolute',
            right: '14px',
            top: '55%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          <XCloseIcon sx={{ fontSize: 14, color: '#666' }} />
        </Box>
      )}

      {React.createElement(SearchBox as React.ComponentType<SearchBoxProps>, {
        accessToken: environment.mapboxApiKey,
        value: searchValue,
        onChange: setSearchValue,
        onRetrieve: handleRetrieve,
        onSuggest: handleSuggest,
        onClear: handleClear,
        options: {
          language: 'en',
          country: 'US',
          limit: 10,
        },
        theme: {
          variables: {
            padding: '16.5px 14px',
            borderRadius: '20px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            boxShadow: 'none',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif',
          },
        },
        placeholder,
        disabled,
      })}

      {/* Custom styling overlay to match MUI TextField */}
      <GlobalStyles
        styles={{
          // Target the SearchBox container
          '[data-testid="searchbox"]': {
            width: '100% !important',
            maxWidth: '100% !important',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif !important',
            boxShadow: 'none !important',
            backgroundColor: 'transparent !important',
            border: '2px solid transparent !important',
            borderRadius: '28px !important',
            height: '56px !important',
            minHeight: '56px !important',
            maxHeight: '56px !important',
            boxSizing: 'border-box !important',
            padding: '0 !important',
          },
          '[data-testid="searchbox"]:hover': {
            backgroundColor: '#F3F4F6 !important',
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
          },
          '[data-testid="searchbox"] input:hover': {
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
          },
          '[data-testid="searchbox"]:focus-within': {
            backgroundColor: 'white !important',
            borderColor: '#9D1500 !important',
            borderWidth: '2px !important',
            borderStyle: 'solid !important',
            outline: 'none !important',
          },
          '[data-testid="searchbox"] input:focus': {
            borderColor: 'transparent !important',
            borderWidth: '1px !important',
            borderStyle: 'solid !important',
          },
          // Target the input field
          '[data-testid="searchbox"] input': {
            fontSize: '16px !important',
            padding: `${size === 'small' ? '8px 12px' : '16.5px 48px'} !important`,
            color: 'rgba(0, 0, 0, 0.87) !important',
            backgroundColor: '#F9FAFB !important',
            height: '56px !important',
            minHeight: '56px !important',
            maxHeight: '56px !important',
            lineHeight: '56px !important',
            boxSizing: 'border-box !important',
            border: '1px solid transparent !important',
            borderRadius: '28px !important',
            outline: 'none !important',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif !important',
            fontWeight: '600 !important',
            letterSpacing: '-0.01em !important',
            width: '100% !important',
          },
          '[data-testid="searchbox"] input::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            opacity: '1 !important',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif !important',
            fontWeight: '600 !important',
            letterSpacing: '-0.01em !important',
            fontSize: '16px !important',
          },
          // Hide ALL default search icons - aggressive approach
          '[data-testid="searchbox"] svg': {
            display: 'none !important',
            visibility: 'hidden !important',
            opacity: '0 !important',
          },
          '[data-testid="searchbox"] button': {
            display: 'none !important',
          },
          '[data-testid="searchbox"] .mapboxgl-ctrl-geocoder--icon-search': {
            display: 'none !important',
          },
          '[data-testid="searchbox"] .mapboxgl-ctrl-geocoder--icon': {
            display: 'none !important',
          },
          '[data-testid="searchbox"] [class*="icon"]': {
            display: 'none !important',
          },
          // Hide any element that might be a search icon
          '[data-testid="searchbox"] [class*="search"]': {
            display: 'none !important',
          },
          '[data-testid="searchbox"] [class*="Search"]': {
            display: 'none !important',
          },
          // Universal icon hiding within the searchbox
          '[data-testid="searchbox"] *': {
            '& svg': {
              display: 'none !important',
              visibility: 'hidden !important',
              opacity: '0 !important',
            },
            '& [class*="icon"]': {
              display: 'none !important',
            },
          },
          // Target the input container
          '[data-testid="searchbox"] > div': {
            height: '56px !important',
            minHeight: '56px !important',
            maxHeight: '56px !important',
            display: 'flex !important',
            alignItems: 'center !important',
            boxSizing: 'border-box !important',
            width: '100% !important',
            borderRadius: '28px !important',
            border: '2px solid transparent !important',
          },
          // Target suggestions dropdown
          '[data-testid="searchbox"] ul': {
            borderRadius: '28px !important',
            marginTop: '8px !important',
            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1) !important',
            border: '1px solid rgba(0, 0, 0, 0.12) !important',
            maxHeight: '300px !important',
            overflowY: 'auto !important',
            '-webkit-overflow-scrolling': 'touch !important',
            zIndex: '1000 !important',
          },
          '[data-testid="searchbox"] li': {
            padding: '10px 16px !important',
            fontSize: '1rem !important',
            color: 'rgba(0, 0, 0, 0.87) !important',
            border: 'none !important',
            minHeight: '48px !important',
            cursor: 'pointer !important',
            touchAction: 'manipulation !important',
          },
          '[data-testid="searchbox"] li:hover': {
            backgroundColor: '#F3F3F3 !important',
            color: 'rgba(0, 0, 0, 0.87) !important',
          },
          // Fallback selectors for different possible class names
          '.mapbox-searchbox': {
            width: '100% !important',
            maxWidth: '100% !important',
            height: '56px !important',
            minHeight: '56px !important',
            maxHeight: '56px !important',
            backgroundColor: '#F9FAFB !important',
            border: '2px solid transparent !important',
            borderRadius: '28px !important',
            boxSizing: 'border-box !important',
          },

          // Generic input targeting
          'input[placeholder*="address"]': {
            height: '56px !important',
            minHeight: '56px !important',
            maxHeight: '56px !important',
            lineHeight: '56px !important',
            boxSizing: 'border-box !important',
            fontSize: '16px !important',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif !important',
            paddingLeft: '48px !important',
          },
          // Additional icon hiding for fallback selectors
          '.mapbox-searchbox svg': {
            display: 'none !important',
          },
          '.mapbox-searchbox button': {
            display: 'none !important',
          },
          '.mapbox-searchbox [class*="icon"]': {
            display: 'none !important',
          },
          // Universal font size override
          '.mapbox-searchbox input': {
            fontSize: '16px !important',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif !important',
            paddingLeft: '48px !important',
            border: '1px solid transparent !important',
            borderRadius: '28px !important',
            backgroundColor: '#F9FAFB !important',
            width: '100% !important',
          },
          // Target any input element within the searchbox
          '[data-testid="searchbox"] * input': {
            fontSize: '16px !important',
            fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif !important',
            paddingLeft: '48px !important',
          },
          // Target the actual SearchBox component
          'div[class*="SearchBox"] svg': {
            display: 'none !important',
            visibility: 'hidden !important',
            opacity: '0 !important',
          },
          'div[class*="searchbox"] svg': {
            display: 'none !important',
            visibility: 'hidden !important',
            opacity: '0 !important',
          },
          // Hide any search icon globally within this component
          'svg[class*="search"]': {
            display: 'none !important',
          },
          'svg[class*="Search"]': {
            display: 'none !important',
          },
          // Additional selectors for better coverage
          'div[class*="search"]:hover': {
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
            backgroundColor: '#F3F4F6 !important',
          },
          'div[class*="search"]:focus-within': {
            borderColor: '#9D1500 !important',
            borderWidth: '1px !important',
            borderStyle: 'solid !important',
          },
          'input:focus': {
            borderColor: 'transparent !important',
            borderWidth: '1px !important',
            borderStyle: 'solid !important',
          },
          // Universal hover selectors
          '[data-testid="searchbox"]:hover, [data-testid="searchbox"] *:hover': {
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
            backgroundColor: '#F3F4F6 !important',
          },
          // Target any container with search-related classes
          '[class*="SearchBox"]:hover, [class*="searchbox"]:hover': {
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
            backgroundColor: '#F3F4F6 !important',
          },
          // Target the input container specifically
          '[data-testid="searchbox"] > div:hover': {
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
            backgroundColor: '#F3F4F6 !important',
          },
          // Universal focus selectors
          '[data-testid="searchbox"]:focus-within, [data-testid="searchbox"] *:focus': {
            borderColor: '#9D1500 !important',
            borderWidth: '1px !important',
            borderStyle: 'solid !important',
            boxSizing: 'border-box !important',
          },
          // Target any container with search-related classes on focus
          '[class*="SearchBox"]:focus-within, [class*="searchbox"]:focus-within': {
            borderColor: '#9D1500 !important',
            borderWidth: '2px !important',
            borderStyle: 'solid !important',
            boxSizing: 'border-box !important',
          },
          // Target the input container specifically on focus
          '[data-testid="searchbox"] > div:focus-within': {
            borderColor: '#9D1500 !important',
            borderWidth: '1px !important',
            borderStyle: 'solid !important',
            boxSizing: 'border-box !important',
          },
        }}
      />
    </Box>
  );
};

export default MapboxAddressAutocomplete;
